/**
 * Burger King reports — region + role column model.
 *
 * BK runs one recruiting team per region ("North 1" … "West 2") and posts the *same* three
 * roles in every region, which is why the org carries ~30 job posts for what is really a
 * 3-role hiring plan. The reports page therefore inverts the usual filter:
 *
 *   - the user picks REGIONS (not job posts), and the jobs are derived from that pick;
 *   - the table columns are ROLES, so the nine regional copies of "Shift Manager" sum into
 *     one column instead of nine.
 *
 * A job's region is its `departmentId` (the owning Team). That is the same key the server
 * uses in `resolveRegions` for the per-candidate `region` field and the "Candidates by Region"
 * panel, and the same key BK's HR completion emails route on — so filter and panel always
 * reconcile. `job.location` (which BK also fills in with the region name, e.g. "West 2") is
 * only a display fallback for a job that has no team attached.
 *
 * Everything here is pure so it can be unit-tested without rendering the page.
 */

/** Region bucket for jobs/candidates with no owning team. Never silently dropped. */
export const UNASSIGNED_REGION_ID = '__unassigned__';
export const UNASSIGNED_REGION_NAME = 'Unassigned';

/** Column bucket for candidates whose job title did not resolve to a role. */
export const UNKNOWN_ROLE_ID = '__unknown_role__';
export const UNKNOWN_ROLE_TITLE = 'Unknown role';

/** "North 2" sorts after "North 1" and before "North 10" — plain sort gets this wrong. */
function naturalCompare(a, b) {
  return String(a).localeCompare(String(b), 'en', { numeric: true, sensitivity: 'base' });
}

/**
 * Closed job posts are excluded from BK reports entirely: if the client closed the requisition,
 * they do not want it padding the columns. This drops the closed job's historical candidates
 * along with it, which is the intended behaviour for this org only.
 */
export function isJobOpen(job) {
  return String(job?.status || '').toLowerCase() !== 'closed';
}

/** Display title for a role column: trimmed, inner whitespace collapsed. Never invents text. */
export function canonicalRoleTitle(title) {
  return String(title || '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Grouping key for a role. Case- and spacing-insensitive so "Shift  Manager" and
 * "shift manager" land in one column, but deliberately NOT fuzzy: a genuinely different
 * title gets its own column so a typo shows up on screen instead of being merged away.
 */
export function roleKey(title) {
  const canonical = canonicalRoleTitle(title);
  return canonical ? canonical.toLowerCase() : UNKNOWN_ROLE_ID;
}

/** The region a job belongs to, by owning team. */
export function jobRegionId(job) {
  const departmentId = String(job?.departmentId || '').trim();
  return departmentId || UNASSIGNED_REGION_ID;
}

/**
 * Region picker options: every team the user may report on, plus an "Unassigned" bucket when
 * open jobs exist with no team. `jobCount` counts only open jobs, so a region whose requisitions
 * are all closed reads as empty rather than looking selectable-but-dead.
 *
 * `includeUnassigned: false` is for a regional (non-admin) user: a job with no team belongs to
 * nobody's region, and the server refuses its screenings for them anyway.
 */
export function buildRegionOptions(departments, jobs, { includeUnassigned = true } = {}) {
  const openJobs = (jobs || []).filter(isJobOpen);
  const countById = new Map();
  openJobs.forEach((job) => {
    const id = jobRegionId(job);
    countById.set(id, (countById.get(id) || 0) + 1);
  });

  const options = (departments || [])
    .map((department) => {
      const id = String(department?._id || department?.id || '').trim();
      if (!id) return null;
      const name = String(department?.name || '').trim();
      return { id, name: name || 'Untitled team', jobCount: countById.get(id) || 0 };
    })
    .filter(Boolean)
    .sort((a, b) => naturalCompare(a.name, b.name));

  // Only offer the fallback bucket when something would actually land in it.
  if (includeUnassigned && countById.get(UNASSIGNED_REGION_ID)) {
    options.push({
      id: UNASSIGNED_REGION_ID,
      name: UNASSIGNED_REGION_NAME,
      jobCount: countById.get(UNASSIGNED_REGION_ID),
    });
  }
  return options;
}

/** Open jobs belonging to any of the selected regions. */
export function scopeJobsToRegions(jobs, selectedRegionIds) {
  const selected = new Set((selectedRegionIds || []).map(String));
  if (selected.size === 0) return [];
  return (jobs || []).filter((job) => isJobOpen(job) && selected.has(jobRegionId(job)));
}

/**
 * The team a screening's candidates count under: its own, else its job's. Mirrors
 * `screeningTeamId` in server/modules/reports/bkReportScope.js — the two must agree, or the
 * server drops screenings the page asked for.
 */
export function screeningRegionId(screening, jobById) {
  const jobId = String(screening?.jobId?._id || screening?.jobId || '');
  const job = jobById ? jobById.get(jobId) : null;
  const departmentId = String(screening?.departmentId || job?.departmentId || '').trim();
  return departmentId || UNASSIGNED_REGION_ID;
}

/**
 * For a regional user: only the screenings owned by one of their selected regions. A West 1 job
 * whose screening belongs to North 2 counts in North 2's report, not West 1's.
 */
export function scopeScreeningsToRegions(screenings, jobs, selectedRegionIds) {
  const selected = new Set((selectedRegionIds || []).map(String));
  if (selected.size === 0) return [];
  const jobById = new Map((jobs || []).map((job) => [String(job?._id || job?.id), job]));
  return (screenings || []).filter((screening) =>
    selected.has(screeningRegionId(screening, jobById))
  );
}

/**
 * Role columns for the given jobs — one per distinct title, alphabetical so the column order
 * is stable across reloads and across regions.
 */
export function buildRoleColumns(jobs) {
  const byKey = new Map();
  (jobs || []).forEach((job) => {
    const key = roleKey(job?.title);
    if (byKey.has(key)) return;
    byKey.set(key, {
      id: key,
      title: key === UNKNOWN_ROLE_ID ? UNKNOWN_ROLE_TITLE : canonicalRoleTitle(job?.title),
    });
  });
  return Array.from(byKey.values()).sort((a, b) => naturalCompare(a.title, b.title));
}

/** Region columns, in the same order the picker shows them. */
export function buildRegionColumns(regionOptions, selectedRegionIds) {
  const selected = new Set((selectedRegionIds || []).map(String));
  return (regionOptions || [])
    .filter((option) => selected.has(String(option.id)))
    .map((option) => ({ id: String(option.id), title: option.name }));
}

/** Which role column a candidate belongs to. `jobTitle` already ships in the report payload. */
export function candidateRoleId(candidate) {
  return roleKey(candidate?.jobTitle);
}

/**
 * Resolver for a candidate's region column.
 *
 * Prefers `regionId` (the team's ObjectId, which the server sends for this org) and falls back
 * to matching the `region` *name* for any candidate saved before `regionId` was added to the
 * payload. Without the name fallback those candidates would silently drop out of region mode.
 */
export function makeRegionResolver(regionOptions) {
  const idToOption = new Map((regionOptions || []).map((o) => [String(o.id), o]));
  const nameToId = new Map(
    (regionOptions || []).map((o) => [String(o.name || '').trim().toLowerCase(), String(o.id)])
  );

  return function resolveRegionId(candidate) {
    const regionId = String(candidate?.regionId || '').trim();
    if (regionId && idToOption.has(regionId)) return regionId;

    const name = String(candidate?.region || '').trim().toLowerCase();
    if (name && nameToId.has(name)) return nameToId.get(name);

    return UNASSIGNED_REGION_ID;
  };
}
